/* eslint-disable no-bitwise */
// An OggOpus stream must start with the OpusHead page, then OpusTags, and both must carry a zero
// granule position. opus-recorder asks the encoder for those header pages over the worklet port
// while the microphone is already feeding it, so they can end up behind the first audio page and
// carrying whatever the encoder had produced by then. A player tolerates it; WhatsApp refuses the
// file saying that on processing it is not an audio type.

const PAGE_HEADER_LENGTH = 27;
const GRANULE_OFFSET = 6;
const SEQUENCE_OFFSET = 18;
const CHECKSUM_OFFSET = 22;
const SEGMENT_COUNT_OFFSET = 26;

const CAPTURE_PATTERN = [0x4f, 0x67, 0x67, 0x53]; // OggS
const OPUS_HEAD = [0x4f, 0x70, 0x75, 0x73, 0x48, 0x65, 0x61, 0x64];
const OPUS_TAGS = [0x4f, 0x70, 0x75, 0x73, 0x54, 0x61, 0x67, 0x73];

const CHECKSUM_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let value = i << 24;
    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 0x80000000 ? (value << 1) ^ 0x04c11db7 : value << 1;
    }
    table[i] = value >>> 0;
  }
  return table;
})();

const matches = (data, offset, pattern) => pattern
  .every((byte, index) => data[offset + index] === byte);

// Ogg checksums cover the whole page with the checksum field zeroed out.
const checksum = (page) => {
  let value = 0;
  for (let i = 0; i < page.length; i += 1) {
    const byte = i >= CHECKSUM_OFFSET && i < CHECKSUM_OFFSET + 4 ? 0 : page[i];
    value = ((value << 8) ^ CHECKSUM_TABLE[((value >>> 24) & 0xff) ^ byte]) >>> 0;
  }
  return value;
};

const parsePages = (data) => {
  const pages = [];
  let offset = 0;
  while (offset < data.length) {
    if (data.length - offset < PAGE_HEADER_LENGTH || !matches(data, offset, CAPTURE_PATTERN)) {
      return null;
    }
    const segmentCount = data[offset + SEGMENT_COUNT_OFFSET];
    const segmentTable = offset + PAGE_HEADER_LENGTH;
    if (segmentTable + segmentCount > data.length) {
      return null;
    }
    let payloadLength = 0;
    for (let i = 0; i < segmentCount; i += 1) {
      payloadLength += data[segmentTable + i];
    }
    const payloadStart = segmentTable + segmentCount;
    const end = payloadStart + payloadLength;
    if (end > data.length) {
      return null;
    }
    pages.push({ start: offset, end, payloadStart });
    offset = end;
  }
  return pages.length ? pages : null;
};

const isHeaderPage = (data, page, signature) => matches(data, page.payloadStart, signature);

export const isValidOggOpus = (data) => {
  const pages = parsePages(data);
  return Boolean(pages) && isHeaderPage(data, pages[0], OPUS_HEAD);
};

const normalizeOggOpus = (data) => {
  const pages = parsePages(data);
  if (!pages) {
    return data;
  }

  const headIndex = pages.findIndex(page => isHeaderPage(data, page, OPUS_HEAD));
  const tagsIndex = pages.findIndex(page => isHeaderPage(data, page, OPUS_TAGS));
  if (headIndex < 0 || tagsIndex < 0) {
    return data;
  }
  const ordered = [
    pages[headIndex],
    pages[tagsIndex],
    ...pages.filter((page, index) => index !== headIndex && index !== tagsIndex),
  ];

  const normalized = new Uint8Array(data.length);
  let offset = 0;
  ordered.forEach((page, sequence) => {
    const length = page.end - page.start;
    normalized.set(data.subarray(page.start, page.end), offset);
    const view = new DataView(normalized.buffer, offset, length);
    // Header pages carry no audio, so their granule position is zero regardless of how
    // much the encoder had already produced when it emitted them.
    if (sequence < 2) {
      view.setUint32(GRANULE_OFFSET, 0, true);
      view.setUint32(GRANULE_OFFSET + 4, 0, true);
    }
    view.setUint32(SEQUENCE_OFFSET, sequence, true);
    view.setUint32(CHECKSUM_OFFSET, 0, true);
    view.setUint32(CHECKSUM_OFFSET, checksum(normalized.subarray(offset, offset + length)), true);
    offset += length;
  });

  return normalized;
};

export default normalizeOggOpus;
